<template>
    <div class="my-24 flex items-center gap-12 flex-wrap">
        <action-button
            no-icon
            @click="toggleWithArchived"
        >
            <template #before>
                <checkbox
                    :checked="tempFilterFields.withArchived"
                    @change="toggleWithArchived"
                />
            </template>
            <span class="first-letter:uppercase">{{ $t('jobs.archived_jobs') }}</span>
        </action-button>

        <action-button
            icon="add"
            @click="toggleFilter"
        >
            {{ $t('applications.add_filter') }}
        </action-button>

        <active-filters
            :filterFields="filterFields"
            @update="updateActiveFilters($event)"
            :default-filters="defaultFilters"
            ref="activeFilters"
        ></active-filters>

        <popup-modal
            v-if="show"
            without-padding
            @close="toggleFilter"
            :close-on-click="false"
        >
            <div class="flex w-768">
                <div class="flex-col w-1/3 flex-none border-r border-primary-20 rounded-l overflow-hidden">
                    <ul class="list-reset">
                        <li v-for="(label, type) in filterTabs"
                            :key="'filter-' + type"
                            :class="[getFilterTabClasses(type)]"
                            class="flex items-center gap-8 px-16 py-12 cursor-pointer hover:bg-primary hover:text-white hover:border-primary"
                            @click="setFilterTab(type)"
                        >
                            <svg-icon
                                class="mr-16"
                                size="16"
                                :type="label.icon"
                                v-if="label.icon"
                            />

                            <span class="first-letter:uppercase">{{ $t(label.title) }}</span>
                        </li>
                    </ul>
                </div>
                <div class="flex flex-col flex-grow pl-32 mr-32 mb-32">
                    <div class="pt-24 mb-32 text-heading-2 font-semibold first-letter:uppercase leading-none">{{ $t('general.filter_on', {subject: $t(filterTabs[filterTabActive].title)}) }}</div>
                    <div class="flex-grow mb-32">
                        <keep-alive>
                            <component
                                :is="filterTabActive"
                                :filterFields="tempFilterFields"
                                :limit="filterTabs[filterTabActive]?.limit"
                            >
                            </component>
                        </keep-alive>
                    </div>

                    <simple-button
                        class="ml-auto"
                        display="secondary"
                        look="primary"
                        icon="replay"
                        @click="applyFilter(true)"
                    >
                        {{ $t('general.apply_filter') }}
                    </simple-button>

                </div>
            </div>
        </popup-modal>
    </div>
</template>

<script>
import PopupModal from "../components/ui/PopupModal";
import SvgIcon from "./ui/SvgIcon";
import SimpleButton from "./ui/SimpleButton";
import * as jobFunctions from "../services/JobFunctionService";
import * as UserService from '../services/UserService'
import {normalizeJson} from "../../shared/helpers";
import User from "../models/User";
import ActiveFilters from "./analytics/ActiveFilters";
import checkbox from "./forms/Checkbox";
import {useRepo} from 'pinia-orm';

// Filters
import FilterApplicationTags from "./filters/FilterApplicationTags";
import FilterJobLocations from "./filters/FilterJobLocations";
import FilterJobResponsibleUser from "./filters/FilterJobResponsibleUser";
import FilterDate from "./filters/FilterDate";
import FilterJobs from "./filters/FilterJobs";
import FilterJobReasons from "./filters/FilterJobReasons";
import FilterApplicationSource from "./filters/FilterApplicationSource";
import FilterOffices from "./filters/FilterOffices";
import FilterTags from "./filters/FilterTags.vue";
import FilterFilterGroups from "./filters/FilterFilterGroups.vue";
import JobFunction from "../models/JobFunction";
import ActionButton from "./ui/ActionButton.vue";

export default {
    name: 'Filters',
    components: {
        ActionButton,
        PopupModal,
        SvgIcon,
        SimpleButton,
        ActiveFilters,
        checkbox,

        // filters
        FilterApplicationTags,
        FilterJobLocations,
        FilterJobResponsibleUser,
        FilterDate,
        FilterJobs,
        FilterTags,
        FilterJobReasons,
        FilterApplicationSource,
        FilterOffices,
        FilterFilterGroups,
    },
    props: {
        filterTabs: {type: Object, required: true},
        filterFields: {type: Object},
        defaultFilters: Object,
    },
    data() {
        return {
            show: false,
            filterTabActive: '',
            tempFilterFields: {},
            filtersAreShown: false,
        }
    },
    computed: {
        filterCount() {
            return Object.keys(this.filterFields).length;
        },
    },
    methods: {
        updateActiveFilters(newFilters) {
            this.tempFilterFields = {...this.tempFilterFields, ...newFilters}
            this.applyFilter(false)
        },
        applyFilter(toggleFilter = false) {
            this.$emit('applyFilters', this.tempFilterFields)
            if (toggleFilter) {
                this.toggleFilter();
                this.$refs.activeFilters.hideFilters();
            }
        },
        toggleFilter() {
            let visible = !this.show;

            // when modal will be visible:
            // - always toggle first tab
            // - reset filter from state
            if (visible) {
                this.setFilterTab(Object.keys(this.filterTabs)[0]);
            } else {
                // If not visible, reset filters
                this.tempFilterFields = {...this.filterFields}
            }

            this.show = visible;
        },
        setFilterTab(tab) {
            this.filterTabActive = tab;
        },
        toggleWithArchived() {
            this.tempFilterFields.withArchived = !this.tempFilterFields.withArchived
            this.applyFilter()
        },
        // TODO: re-add this after communication to customers
        // toggleWithAnonymised() {
        //     this.tempFilterFields.withAnonymised = !this.tempFilterFields.withAnonymised
        //     this.applyFilter()
        // },
        getFilterTabClasses(tab) {
            if (this.filterTabActive === '') {
                this.filterTabActive = tab;
            }

            let classes = [],
                isLastTab = false,
                keys = Object.keys(this.filterTabs)
            ;

            if (this.filterTabActive === tab) {
                classes.push('bg-primary text-white');
            } else {
                classes.push('text-black');
            }

            for (let i in keys) {
                if ((keys[i] === tab) && (i === (keys.length - 1))) {
                    isLastTab = true;
                    break;
                }
            }

            if (!isLastTab) {
                classes.push('border-b');
                if (this.filterTabActive === tab) {
                    classes.push('border-primary');
                } else {
                    classes.push('border-primary-20');
                }
            }

            return classes;
        },
        fetch() {
            jobFunctions.index()
                .then(result => {
                    let data = normalizeJson(result.data);

                    // possible fix on TypeError .forEach is not a function
                    // .forEach requires an Array
                    if (data && typeof (data) === "string") {
                        data = JSON.parse(data)
                    }

                    // set translated title so we can order on it
                    data.forEach(job => {
                        job.title_translated = job.title ? (job.title[this.locale] || '') : '';
                        job.city = this.jobLocation(job);
                    })

                    return useRepo(JobFunction).save(data);
                })
            UserService.index()
                .then(result => {
                    return useRepo(User).save(normalizeJson(result.data));
                })
        },
        jobLocation(job) {
            let location = job.city;
            if (job.location)
                location = job.location.city;

            return location || null;
        },
    },
    watch: {
        'filterFields'() {
            this.tempFilterFields = {...this.filterFields};
        }
    },
    created() {
        this.tempFilterFields = {...this.filterFields};
    },
    mounted() {
        this.fetch()
    }
}
</script>

<style scoped>

</style>
